import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, aboutContent } from '@/mocks/site';

export default function AboutPage() {
  const pageTitle = `关于我们 - ${siteInfo.name}`;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutContent.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: pageTitle,
    description: aboutContent.brandIntro,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@id': `${siteInfo.url}/#website`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <div className="pt-20 bg-background-50 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <div className="pb-14">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
              <div className="lg:col-span-2">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
                  关于我们
                </h1>
                <p className="text-foreground-700 leading-relaxed mb-6 text-base">
                  {aboutContent.brandIntro}
                </p>
                <p className="text-foreground-700 leading-relaxed mb-6 text-base">
                  {aboutContent.mission}
                </p>
                <p className="text-foreground-700 leading-relaxed text-base">
                  {aboutContent.teamDescription}
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70 self-start">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary-500 flex items-center justify-center">
                    <span className="font-heading text-3xl font-bold text-background-50">爆</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">{siteInfo.name}</h3>
                  <p className="text-sm text-foreground-500">成立于 {siteInfo.foundingYear} 年</p>
                </div>
              </div>
            </div>

            <section className="mb-14">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  品牌口碑
                </h2>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-8">
                <p className="text-foreground-700 leading-relaxed text-base">
                  {aboutContent.reputation}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8">
                  {[
                    { value: '5000万+', label: '累计用户' },
                    { value: '200+', label: '日均更新' },
                    { value: '100+', label: '合作媒体' },
                    { value: '7×24', label: '全天候更新' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{stat.value}</p>
                      <p className="text-sm text-foreground-500 mt-1 font-label">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  联系我们
                </h2>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-8">
                <p className="text-foreground-700 leading-relaxed mb-8">
                  {aboutContent.contactIntro}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-950 mb-1">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-950 mb-1">联系电话</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-label font-semibold text-foreground-950 mb-1">办公地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  常见问题
                </h2>
              </div>
              <div className="flex flex-col gap-4">
                {aboutContent.faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors list-none">
                      <h3 className="font-label text-base font-semibold text-foreground-950 pr-8">
                        {faq.question}
                      </h3>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-500 group-open:text-background-50 transition-all">
                        <i className="ri-add-line text-sm group-open:rotate-45 transition-transform"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}